/**
 * 曝光追踪模块
 * 基于 IntersectionObserver + 状态机驱动的即时上报模式
 * 每次有效曝光结束（元素离开视口）时立即上报，不再跨周期累计数据
 * 路由变化/页面卸载时兜底上报仍在视口内的曝光中元素
 */

import type { TrackData, BusinessParams, CommonParams, RoutePathResolver, ReferrerResolver } from '../types'
import type { ExposeMeta } from '../collectors/exposure'
import type { ExposeEventParams } from '../types/event'
import { collectExposeEvent, getElementInfo } from '../collectors/exposure'
import { refreshDynamicFields } from '../collectors/common'
import { resolveRoutePath, getPageId, resolveReferrerPage } from '../collectors/page'
import { Reporter } from '../reporter'
import type { DynamicConfigManager } from '../core/dynamic-config'
import { DATA_ATTRS } from '../core/constants'
import { AutoExposureScanner } from './auto-exposure-scanner'
import type { ScanMetrics } from './auto-exposure-scanner'
import type { RouteChangeManager, RouteChangeInfo, Unsubscribe } from '../core/route-change-manager'
import type { PageModule } from './page'
import { extractAttributes, collectMissingFields, sleep, scheduleIdle } from '../utils'

export interface ExposureModuleConfig {
  enabled: boolean
  threshold?: number  // 曝光阈值，默认 0.5
  delay?: number      // 曝光延迟（ms），默认 100
  debug?: boolean
  routePathResolver?: RoutePathResolver
  referrerResolver?: ReferrerResolver
  // 自动曝光扫描
  enableAutoExposure?: boolean
  autoExposureMaxElements?: number
  autoExposureScanRoot?: string
}

interface ExposureRecord {
  element: Element
  meta?: ExposeMeta
  businessParams?: BusinessParams
  // 单次上报数据（每次离开视口时重置）
  exposeTimes: number                    // 本次上报的曝光次数（固定为 1）
  totalDuration: number                  // 本次上报的曝光时长(ms)
  // 状态机
  state: 'idle' | 'pending' | 'exposing' // 状态机当前状态，idle: 空闲状态，pending: 等待曝光，exposing: 曝光中
  currentStartTime: number               // 当前曝光期开始时间（delay 触发时记录）
  timer: ReturnType<typeof setTimeout> | null
  lastEntry: IntersectionObserverEntry | null  // 最近 entry（上报时使用）
}

/**
 * 曝光追踪模块
 */
export class ExposureModule {
  private config: ExposureModuleConfig
  private reporter: Reporter
  private commonParams: CommonParams
  private dynamicConfigManager?: DynamicConfigManager
  /** 观察者实例  */
  private observer: IntersectionObserver | null = null
  private isInitialized = false
  private pageModule?: PageModule

  // 曝光记录 Map
  private exposureRecords = new Map<Element, ExposureRecord>()

  // 自定义元数据存储
  private elementMeta = new WeakMap<Element, { 
    meta?: ExposeMeta; 
    businessParams?: BusinessParams;
    cachedParams?: {
      eleId?: string;
      eleParams?: Record<string, unknown>;
      listItemFilter?: Record<string, unknown>;
    }
  }>()

  // 曝光 ID 自增计数器（页面级唯一）
  private exposeIdCounter = 0

  // 自动曝光扫描器
  private autoScanner: AutoExposureScanner | null = null

  // 自动扫描注册的元素集合
  private autoObservedElements = new Set<Element>()

  // 路由变更管理器
  private routeChangeManager?: RouteChangeManager

  // 路由订阅取消函数
  private unsubscribeRouteChange: Unsubscribe | null = null

  // 当前路由路径（用于在路由变化时保持正确的页面上下文）
  private currentRoutePath: string = ''
  // 当前页面 ID
  private currentPageId: string = ''

  // 绑定的页面卸载处理函数（用于移除监听）
  private boundHandlePageUnload: (() => void) | null = null

  constructor(
    config: ExposureModuleConfig,
    reporter: Reporter,
    commonParams: CommonParams,
    routeChangeManager?: RouteChangeManager,
    dynamicConfigManager?: DynamicConfigManager,
    pageModule?: PageModule
  ) {
    this.config = config
    this.reporter = reporter
    this.commonParams = commonParams
    this.routeChangeManager = routeChangeManager
    this.dynamicConfigManager = dynamicConfigManager
    this.pageModule = pageModule
  }

  /**
   * 初始化模块
   */
  init(): void {
    if (!this.config.enabled || this.isInitialized) return
    if (typeof IntersectionObserver === 'undefined') {
      this.log('IntersectionObserver not supported')
      return
    }

    this.isInitialized = true

    const threshold = this.config.threshold ?? 0.5
    this.observer = new IntersectionObserver(
      this.handleIntersection,
      {
        threshold: [0, threshold],
        rootMargin: '0px',
      }
    )

    this.log('Exposure 模块已初始化', { threshold })

    // 初始化当前路由信息
    this.currentRoutePath = resolveRoutePath(this.config.routePathResolver)
    this.currentPageId = getPageId(this.currentRoutePath)

    // 订阅路由变化（必须在 autoScanner.start() 之前注册，确保 flush 先于 reset 执行）
    if (this.routeChangeManager && this.dynamicConfigManager) {
      this.unsubscribeRouteChange = this.routeChangeManager.subscribe(this.handleRouteChange)
    }

    // 监听页面卸载，确保销毁前上报仍在曝光中的元素
    this.setupUnloadListener()

    /**
     * 初始化自动曝光扫描器
     * 需要同时满足以下条件：
     * 1. 配置了 enableAutoExposure
     * 2. 有路由变更管理器
     * 3. 有动态配置管理器
     */
    if (this.config.enableAutoExposure && this.routeChangeManager && this.dynamicConfigManager) {
      this.autoScanner = new AutoExposureScanner(
        {
          maxElements: this.config.autoExposureMaxElements ?? 100,
          scanRoot: this.config.autoExposureScanRoot ?? '',
          debug: this.config.debug ?? false,
        },
        {
          onElementsDiscovered: (elements) => {
            for (const el of elements) {
              this.observe(el, { exposeTrigger: 'auto' })
              this.autoObservedElements.add(el)
            }
          },
          onElementsRemoved: (elements) => {
            for (const el of elements) {
              this.unobserve(el)
              this.autoObservedElements.delete(el)
            }
          },
          isElementObserved: (el) => this.exposureRecords.has(el),
          getObservedCount: () => this.exposureRecords.size,
          resetExposures: () => this.resetAllExposures(),
        },
        this.routeChangeManager
      )
      this.autoScanner.start()
      this.log('Auto exposure scanner started')
    }
  }

  /**
   * 更新公共参数引用
   */
  updateCommonParams(commonParams: CommonParams): void {
    this.commonParams = commonParams
  }

  /**
   * 观察元素曝光
   * @param element 要观察的元素
   * @param meta 元数据（可选）
   * @param businessParams 业务参数（可选）
   */
  observe(element: Element, meta?: ExposeMeta, businessParams?: BusinessParams): number | undefined {
    if (!this.observer || !element) return undefined
    // 无动态配置管理器时不注册观察
    if (!this.dynamicConfigManager) return undefined

    // 动态配置模式下按需检查
    const eleId = (element as HTMLElement).getAttribute?.(DATA_ATTRS.ELE_ID)
    if (!eleId) {
      return undefined
    }

    const routePath = resolveRoutePath(this.config.routePathResolver)
    const pageId = getPageId(routePath)
    if (!this.dynamicConfigManager.shouldTrackEvent(pageId, eleId, 'expose')) {
      // this.log('曝光事件不在动态配置中，跳过', { pageId, eleId })
      return undefined
    }

    // 开始观察
    this.observer.observe(element)
    
    // 延迟 100ms 后在空闲时段采集并缓存元素参数
    sleep(100).then(() => {
      scheduleIdle(() => {
        this.collectAndCacheElementParams(element, meta, businessParams)
      })
      
      // 2 秒后再次在空闲时段采集并缓存元素参数(捕获动态加载的内容)
      sleep(2000).then(() => {
        scheduleIdle(() => {
          this.collectAndCacheElementParams(element, meta, businessParams)
        })
      })
    })

    // 生成页面唯一曝光 ID
    const exposeId = ++this.exposeIdCounter
    ;(element as HTMLElement).setAttribute(DATA_ATTRS.EXPOSE_ID, String(exposeId))

    // this.log('Started observing element', { ...meta, exposeId })

    return exposeId
  }

  /**
   * 采集并缓存元素参数
   * @param element 元素
   * @param eleId 元素ID(已验证存在)
   * @param meta 元数据
   * @param businessParams 业务参数
   */
  private collectAndCacheElementParams(
    element: Element,
    meta?: ExposeMeta,
    businessParams?: BusinessParams
  ): void {
    const elementInfo = getElementInfo(element as HTMLElement)
    let listItemFilter: Record<string, unknown> | undefined = elementInfo.listItemFilter

    // 存储元数据(扩展为包含缓存参数)
    this.elementMeta.set(element, { 
      meta, 
      businessParams,
      cachedParams: {
        eleId: elementInfo.eleId ? String(elementInfo.eleId) : undefined,
        eleParams: elementInfo.eleParams,
        listItemFilter
      }
    })
  }

  /**
   * 停止观察元素
   * @param element 元素
   */
  unobserve(element: Element): void {
    if (!this.observer || !element) return

    this.observer.unobserve(element)
    ;(element as HTMLElement).removeAttribute?.(DATA_ATTRS.EXPOSE_ID)
    this.elementMeta.delete(element)

    // 清理记录
    const record = this.exposureRecords.get(element)
    if (record?.timer) {
      clearTimeout(record.timer)
    }
    this.exposureRecords.delete(element)

    this.log('Stopped observing element')
  }

  /**
   * 处理交叉观察回调（状态机驱动）
   *
   * 状态转换：
   *   idle ──进入视口──→ pending ──delay触发──→ exposing
   *     ↑    离开(清timer)   ↑                      │
   *     └────────────────────┘   离开(即时上报)       │
   *     ↑                                            │
   *     └────────────────────────────────────────────┘
   */
  private handleIntersection = (entries: IntersectionObserverEntry[]): void => {
    const threshold = this.config.threshold ?? 0.5
    const delay = this.config.delay ?? 100

    for (const entry of entries) {
      const element = entry.target
      const record = this.exposureRecords.get(element)

      if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
        // ═══ 元素进入视口 ═══
        if (!record) {
          // 首次发现：创建新 record，state='pending'
          const metaCache = this.elementMeta.get(element)
          const newRecord: ExposureRecord = {
            element,
            meta: metaCache?.meta,
            businessParams: metaCache?.businessParams,
            exposeTimes: 0,
            totalDuration: 0,
            state: 'pending',
            currentStartTime: 0,
            timer: null,
            lastEntry: entry,
          }

          /**
           * 设置延迟确认
           * 默认延时100ms
           * 当元素进入视口时，设置一个定时器确认有效曝光，
           * 若元素在定时器触发前离开视口，则取消本次曝光
           */
          newRecord.timer = setTimeout(() => this.handleDelayComplete(newRecord), delay)
          this.exposureRecords.set(element, newRecord)
        } else if (record.state === 'idle') {
          // 再次进入视口：idle → pending
          record.state = 'pending'
          record.lastEntry = entry
          record.timer = setTimeout(() => this.handleDelayComplete(record), delay)
        }
        // state 为 pending 或 exposing 时忽略（已在跟踪中）
      } else {
        // ═══ 元素离开视口 ═══
        if (!record) continue

        if (record.state === 'pending') {
          // 未通过 delay 检查，取消本次曝光
          if (record.timer) clearTimeout(record.timer)
          record.timer = null
          record.state = 'idle'
        } else if (record.state === 'exposing') {
          // 曝光结束，计算本次时长并立即上报
          const duration = Date.now() - record.currentStartTime
          record.totalDuration = duration
          record.exposeTimes = 1
          this.reportAccumulatedExposure(record)
          // 重置为 idle，清空数据
          record.totalDuration = 0
          record.exposeTimes = 0
          record.state = 'idle'
        }
      }
    }
  }

  /**
   * delay 定时器触发后确认有效曝光开始
   */
  private handleDelayComplete(record: ExposureRecord): void {
    record.timer = null
    record.state = 'exposing'
    record.exposeTimes++
    record.currentStartTime = Date.now()  // duration 从此刻开始计算
    // this.log('有效曝光 +1', { exposeTimes: record.exposeTimes, meta: record.meta })
  }

  /**
   * 处理路由变化：先 flush 仍在曝光中的元素，再重置状态
   */
  private handleRouteChange = (_info: RouteChangeInfo): void => {
    // 先 flush 曝光数据（此时使用保存的旧路由信息）
    this.flushAccumulatedExposures()

    // 更新当前路由信息（在 flush 之后，确保曝光数据关联到正确的页面）
    this.currentRoutePath = resolveRoutePath(this.config.routePathResolver)
    this.currentPageId = getPageId(this.currentRoutePath)

    // 有 AutoExposureScanner 时由其调用 resetAllExposures()
    // 无 AutoExposureScanner 时自行重置
    if (!this.autoScanner) {
      this.resetAllExposures()
    }
  }

  /**
   * 设置页面卸载监听
   * 确保页面销毁前上报仍在曝光中的元素数据
   */
  private setupUnloadListener(): void {
    if (typeof window === 'undefined') return

    this.boundHandlePageUnload = this.handlePageUnload.bind(this)

    window.addEventListener('beforeunload', this.boundHandlePageUnload)
    window.addEventListener('pagehide', this.boundHandlePageUnload)
    // document.addEventListener('visibilitychange', this.handleVisibilityChange)
  }

  /**
   * 处理页面可见性变化
   */
  private handleVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') {
      this.handlePageUnload()
    }
  }

  /**
   * 处理页面卸载：上报仍在曝光中的元素数据
   */
  private handlePageUnload(): void {
    this.flushAccumulatedExposures()
  }

  /**
   * 兜底上报仍在曝光中的元素（路由变化/页面卸载/销毁时调用）
   * 已离开视口的元素在离开时已即时上报，此处仅处理 exposing 状态的元素
   */
  private flushAccumulatedExposures(): void {
    if (!this.dynamicConfigManager) return

    for (const [_element, record] of this.exposureRecords) {
      // 清除可能残留的 pending timer
      if (record.timer) {
        clearTimeout(record.timer)
        record.timer = null
      }
      // 只结算当前正在曝光中的元素
      if (record.state === 'exposing') {
        const duration = Date.now() - record.currentStartTime
        record.totalDuration = duration
        record.exposeTimes = 1
        this.reportAccumulatedExposure(record)
        record.state = 'idle'
      }
    }
  }

  /**
   * 构建 TrackData 并上报单次曝光数据
   *
   * 参数采集策略详见 resolveExposeParams：
   *   - 元素存活：使用 lastEntry 重新采集最新 DOM 参数
   *   - 元素销毁 / eleId 为空：使用 elementMeta 中的缓存参数兜底
   *   - display:none 等视觉隐藏判断暂未启用，详见 isElementDestroyed
   */
  private reportAccumulatedExposure(record: ExposureRecord): void {
    if (!this.dynamicConfigManager) return

    const { eleId, listItemFilter, exposeParams } = this.resolveExposeParams(record)

    // eleId 仍为空（缓存也未命中）则跳过上报
    if (!eleId) return

    // 使用保存的路由信息，确保路由切换时上报正确的页面 ID
    const pageId = this.currentPageId
    if (!this.dynamicConfigManager.shouldTrackEvent(pageId, eleId, 'expose')) return

    const eventConfig = this.dynamicConfigManager.getEventConfig(pageId, eleId, 'expose')
    const eleConfig = this.dynamicConfigManager.getElementConfig(pageId, eleId)
    const pageConfig = this.dynamicConfigManager.getPageConfig(pageId)

    // 获取页面级参数与公共参数
    const pageParams = this.pageModule?.getPageParams() ?? null
    const networkRecords = this.pageModule?.getCurrentPageRequests() ?? null
    const common = refreshDynamicFields(this.commonParams)

    // 提取 attributes 参数
    const attributesData = extractAttributes(eventConfig?.fields || [], {
      pageParams: pageParams || {},
      eleParams: exposeParams.exposeEleParams,
      businessParams: record.businessParams,
      apis: networkRecords || [],
    }, listItemFilter)

    // 收集 fieldList 中 value 为 null 或 undefined 的字段信息
    const missingFields = collectMissingFields(attributesData.fieldList)

    const data: TrackData = {
      common,
      event: {
        eventType: 'expose',
        eventName: eventConfig?.eventName || '',
        eventCode: eventConfig?.eventCode || '',
        expose: {
          eleId,
          eleBusinessName: eleConfig?.eleBusinessName,
          exposeDuration: record.totalDuration,
          exposeEleName: exposeParams.exposeEleName,
          exposeTimes: record.exposeTimes,
          exposeTrigger: exposeParams.exposeTrigger,
        },
        page: {
          pageId,
          pageName: pageConfig?.pageName || document.title || '学天教育',
          currentUrl: window.location.href,
          referrerPage: resolveReferrerPage(this.config.referrerResolver),
        },
      },
      business: {
        attributes: attributesData,
        missingFields,
        eventResult: {},
      },
    }

    this.reporter.report(data)
    this.log('Exposure reported', {
      eleId,
      exposeDuration: record.totalDuration,
      elementDestroyed: this.isElementDestroyed(record.element),
    })
  }

  /**
   * 解析单次上报所需的曝光参数
   *
   * 对应 reportAccumulatedExposure 注释中的 4 种情况：
   *   1. 元素仍在视口：使用 lastEntry 重新采集最新 DOM 参数
   *   2. 元素已离开视口：使用 lastEntry 重新采集最新 DOM 参数（由 handleIntersection 即时触发上报）
   *   3. 元素的 eleId 为空：视为销毁，使用 elementMeta 中的缓存参数兜底，触发上报
   *
   * 元素已从 DOM 移除（!isConnected）走缓存兜底分支，触发上报
   */
  private resolveExposeParams(record: ExposureRecord): {
    eleId: string | undefined
    listItemFilter: Record<string, unknown> | undefined
    exposeParams: ExposeEventParams
  } {
    const element = record.element

    if (!this.isElementDestroyed(element)) {
      // 情况 1、2：元素存活，使用 lastEntry 重新采集
      const elementInfo = getElementInfo(element as HTMLElement)
      const entry = record.lastEntry || this.createMockEntry(element)
      const exposeParams = collectExposeEvent(
        entry,
        record.meta,
        record.totalDuration,   // 本次曝光时长
        record.exposeTimes      // 本次曝光次数（固定为 1）
      )
      return {
        eleId: elementInfo.eleId || undefined,
        listItemFilter: elementInfo.listItemFilter,
        exposeParams,
      }
    }

    // 情况 3 + 元素已销毁：使用缓存参数
    const cached = this.elementMeta.get(element)?.cachedParams
    return {
      eleId: cached?.eleId,
      listItemFilter: cached?.listItemFilter,
      exposeParams: {
        exposeId: undefined, // 元素视为销毁，不暴露 exposeId
        exposeEleName: record.meta?.exposeEleName,
        exposeEleParams: cached?.eleParams,
        exposeTrigger: record.meta?.exposeTrigger || 'manual',
        exposeDuration: record.totalDuration,
        exposeTimes: record.exposeTimes,
        isClickAfter: false,
      },
    }
  }

  /**
   * 判断元素是否应视为"销毁"（应使用缓存参数兜底）
   * 当前启用的判断条件：
   *   - 元素已从 DOM 移除（!isConnected）
   *   - 元素的 eleId 属性为空（被清除或重新挂载未设置）
   *
   * 注：display: none / visibility: hidden / 尺寸为 0 等"视觉隐藏"容易存在误判，禁止使用该判断条件。
   * 该场景适用的判断条件：清空元素的 eleId 属性
   */
  private isElementDestroyed(element: Element): boolean {
    if (!element.isConnected) return true

    const htmlEl = element as HTMLElement
    const eleId = htmlEl.getAttribute?.(DATA_ATTRS.ELE_ID)
    if (!eleId) return true

    // [暂未启用] 视觉隐藏判断，启用前请阅读上方注释中的原因与补全要点
    // if (typeof window !== 'undefined' && typeof window.getComputedStyle === 'function') {
    //   if (window.getComputedStyle(htmlEl).display === 'none') return true
    // }

    return false
  }

  /**
   * 构造模拟的 IntersectionObserverEntry（当 lastEntry 不可用时）
   */
  private createMockEntry(element: Element): IntersectionObserverEntry {
    const rect = element.getBoundingClientRect()
    return {
      target: element,
      boundingClientRect: rect,
      intersectionRatio: 1,
      intersectionRect: rect,
      isIntersecting: true,
      rootBounds: null,
      time: Date.now(),
    } as IntersectionObserverEntry
  }

  /**
   * 手动触发曝光上报
   * @param element 元素
   */
  triggerExposure(element: Element): void {
    if (!element) return
    if (!this.dynamicConfigManager) return
    // 暂时保留方法签名，未来可扩展
  }

  /**
   * 重置曝光状态（允许元素再次曝光）
   * @param element 元素
   */
  resetExposure(element: Element): void {
    const record = this.exposureRecords.get(element)
    if (record?.timer) clearTimeout(record.timer)
    this.exposureRecords.delete(element)
  }

  /**
   * 重置所有曝光状态（路由变化时由 AutoExposureScanner 调用）
   */
  resetAllExposures(): void {
    // 清理所有定时器
    for (const record of this.exposureRecords.values()) {
      if (record.timer) clearTimeout(record.timer)
    }
    this.exposureRecords.clear()
    this.autoObservedElements.clear()

    // 重建 IntersectionObserver（disconnect 后已有的 observe 注册全部失效）
    if (this.observer) {
      this.observer.disconnect()
      const threshold = this.config.threshold ?? 0.5
      this.observer = new IntersectionObserver(this.handleIntersection, {
        threshold: [0, threshold],
        rootMargin: '0px',
      })
    }

    this.log('All exposures reset')
  }

  /**
   * 获取自动扫描性能指标
   */
  getAutoScanMetrics(): ScanMetrics | null {
    return this.autoScanner?.getMetrics() ?? null
  }

  /**
   * 销毁模块
   */
  destroy(): void {
    if (!this.isInitialized) return

    // 销毁前 flush 仍在曝光中的元素
    this.flushAccumulatedExposures()

    // 取消路由订阅
    this.unsubscribeRouteChange?.()
    this.unsubscribeRouteChange = null

    // 移除页面卸载事件监听
    if (typeof window !== 'undefined' && this.boundHandlePageUnload) {
      window.removeEventListener('beforeunload', this.boundHandlePageUnload)
      window.removeEventListener('pagehide', this.boundHandlePageUnload)
      // document.removeEventListener('visibilitychange', this.handleVisibilityChange)
      this.boundHandlePageUnload = null
    }

    // 销毁自动扫描器
    this.autoScanner?.destroy()
    this.autoScanner = null
    this.autoObservedElements.clear()

    // 清理所有定时器
    for (const record of this.exposureRecords.values()) {
      if (record.timer) {
        clearTimeout(record.timer)
      }
    }
    this.exposureRecords.clear()

    // 断开观察器
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }

    this.isInitialized = false
    this.log('Exposure 模块已销毁')
  }

  /**
   * 调试
   */
  private log(message: string, data?: unknown): void {
    if (this.config.debug) {
      console.log('%c[Tracker Exposure 调试] %c ' + message, 'background: rgb(82, 155, 46); color: #fff;', 'color: inherit;', data)
      
    }
  }
}
